import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import MarketingNav from '../components/MarketingNav';
import MarketingFooter from '../components/MarketingFooter';

const API_BASE = process.env.REACT_APP_API_URL || 'https://backend-production-9457.up.railway.app';

const C = { void:'#030A3C', void2:'#060D4A', void3:'#0A1260', void4:'#0D1570', cyan:'#00FFFF', cyanDim:'rgba(0,255,255,0.10)', cyanMid:'rgba(0,255,255,0.30)', cyanBorder:'rgba(0,255,255,0.15)', magma:'#FF5722', magmaDim:'rgba(255,87,34,0.12)', white:'#F8F9FA', muted:'rgba(248,249,250,0.72)', muted2:'rgba(248,249,250,0.72)', border:'rgba(0,255,255,0.08)', border2:'rgba(0,255,255,0.15)', green:'#2A8B7A' };

/* ─── Particles ─── */
function Particles() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const frags = [];
    for (let i = 0; i < 28; i++) {
      const p = document.createElement('div');
      const size  = Math.random() * 2.5 + 0.8;
      const dur   = Math.random() * 12 + 8;
      const delay = Math.random() * -20;
      const drift = (Math.random() - 0.5) * 80;
      const isMagma = Math.random() < 0.12;
      p.style.cssText = `
        position:absolute; border-radius:50%;
        width:${size}px; height:${size}px;
        left:${Math.random()*100}%;
        bottom:${Math.random()*20}%;
        background:${isMagma ? '#FF5722' : '#00FFFF'};
        animation: scqFloat ${dur}s linear ${delay}s infinite;
        --drift:${drift}px;
        opacity:0;
        pointer-events:none;
      `;
      el.appendChild(p);
      frags.push(p);
    }
    return () => frags.forEach(p => p.remove());
  }, []);
  return <div ref={ref} style={s.particles} />;
}

/* ─── Floating insight cards ─── */
const INSIGHT_CARDS = [
  {
    pos: { left:'4%', top:'28%' },
    dur: '6s',
    delay: '0s',
    label: 'Theme identified',
    theme: 'Onboarding friction',
    quote: '"I didn\'t know where to start - the first screen was just overwhelming."',
    meta: 'P4 · 03:22',
    bars: [28, 18, 8],
    severity: 'High',
  },
  {
    pos: { right:'4%', top:'36%' },
    dur: '7.5s',
    delay: '-2s',
    label: 'Key finding',
    theme: 'Trust signals missing',
    quote: '"I wanted to see proof it worked before I gave it my data."',
    meta: 'P2 · 11:47',
    severity: 'Critical',
  },
  {
    pos: { right:'3%', top:'62%' },
    dur: '5.5s',
    delay: '-1s',
    width: 210,
    label: 'Synthesis complete',
    theme: '3 themes · 14 quotes',
    quote: 'Report ready - 47 min processing time.',
  },
];

function InsightCard({ card }) {
  return (
    <div style={{
      ...s.insightCard,
      ...card.pos,
      width: card.width || 230,
      animation: `scqCardFloat ${card.dur} ease-in-out ${card.delay} infinite`,
    }}>
      <div style={s.icLabel}>{card.label}</div>
      <div style={s.icTheme}>{card.theme}</div>
      <div style={s.icQuote}>{card.quote}</div>
      {(card.meta || card.severity) && (
        <div style={s.icMeta}>
          {card.meta && <span>{card.meta}</span>}
          {card.bars && (
            <div style={s.icBars}>
              {card.bars.map((w, i) => (
                <div key={i} style={{...s.icBar, width:w, opacity: [0.9,0.5,0.2][i]}} />
              ))}
            </div>
          )}
          {card.severity && <span style={s.icSeverity}>● {card.severity}</span>}
        </div>
      )}
    </div>
  );
}

/* ─── Stats ─── */
const STATS = [
  { n: '1,240', suffix: '+', label: 'Interviews processed in beta' },
  { n: '47',    suffix: 'm',  label: 'Avg. synthesis time per session' },
  { n: '2.1',   suffix: 'd',  label: 'Avg. time saved per study' },
  { n: '0',     suffix: '%',  label: 'Findings without a source citation' },
];

/* ─── Logos ─── */
const LOGOS = [
  { name:'Spotify',     color:'#1ED760' },
  { name:'Intercom',    color:'#2563EB' },
  { name:'Wise',        color:'#00B9FF' },
  { name:'Hotjar',      color:'#FF6B00' },
  { name:'Maze',        color:'#7C3AED' },
  { name:'UserTesting', color:'#0EA5E9' },
  { name:'Figma',       color:'#F59E0B' },
];

/* ─── Testimonials ─── */
const TESTIMONIALS = [
  {
    featured: true,
    text: <>The synthesis phase used to take me <strong style={{color:C.cyan, fontWeight:500}}>two full days</strong> per study. SenseCritiq does it before my debrief. And every quote actually links back to the source - that's the part I didn't expect to matter as much as it does.</>,
    initials: 'SR',
    name: 'Sophie R.',
    role: 'Senior UX Researcher · Fintech, London',
  },
  {
    text: <>I was sceptical about AI synthesis - I've been burned by tools that just <strong style={{color:C.cyan, fontWeight:500}}>make things up</strong>. SenseCritiq is different. Every theme cites the exact moment in the transcript. My stakeholders trust it.</>,
    initials: 'MK',
    name: 'Marcus K.',
    role: 'ResearchOps Lead · SaaS, Berlin',
  },
  {
    text: <>We ran six interviews in a week. Normally that's <strong style={{color:C.cyan, fontWeight:500}}>two weeks of synthesis</strong> work. With SenseCritiq we had a shareable report the same afternoon. It's changed how we scope research projects entirely.</>,
    initials: 'AL',
    name: 'Anika L.',
    role: 'UX Research Manager · E-commerce, Amsterdam',
  },
];

function HeroWaitlist() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/portal/waitlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      if (res.ok) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: 'waitlist_signup' });
        setDone(true);
      } else {
        const d = await res.json().catch(() => ({}));
        setError(d.detail || 'Something went wrong — try again.');
      }
    } catch { setError('Network error — please try again.'); }
    finally { setLoading(false); }
  }

  if (done) return (
    <div style={s.wlDone}>
      <span style={{color: C.cyan, fontWeight: 700, marginRight: 8}}>✓</span>
      You're on the list — we'll be in touch soon.
    </div>
  );

  return (
    <form onSubmit={handleSubmit} style={s.wlForm}>
      <input
        type="email"
        required
        placeholder="your@email.com"
        value={email}
        onChange={e => setEmail(e.target.value)}
        style={s.wlInput}
      />
      <button type="submit" disabled={loading} style={s.wlBtn}>
        {loading ? 'Joining…' : 'Join the waitlist'}
      </button>
      {error && <p style={s.wlError}>{error}</p>}
    </form>
  );
}

export default function Home() {
  return (
    <div style={s.root}>
      <MarketingNav />

      {/* ── HERO ── */}
      <div style={s.hero} className="scq-hero">
        <div style={s.heroBg} />
        <Particles />
        <div className="scq-hero-visual" style={{position:'absolute',inset:0,pointerEvents:'none'}}>
          {INSIGHT_CARDS.map((card, i) => <InsightCard key={i} card={card} />)}
        </div>

        <div style={s.heroContent}>
          {/* Pulsing badge */}
          <div style={s.badge}>
            <span style={s.badgeDot} />
            Private beta · Limited spots available
          </div>

          <h1 style={s.h1}>
            Map user emotion.<br/>
            <span style={s.h1Accent}>Reach certainty.</span>
          </h1>

          <p style={s.heroSub}>
            SenseCritiq turns hours of raw interviews into structured themes, verbatim evidence, and
            shareable reports — organised by project, searchable across your entire research history,
            and queryable from Claude or the dashboard.
          </p>

          <div id="waitlist" style={{scrollMarginTop: 80}}>
            <HeroWaitlist />
            <div style={{marginTop: 24}}>
              <Link to="/how-it-works" style={s.ctaGhostWide}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{flexShrink:0}}><circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.4"/><path d="M6 8h4M8 6l2 2-2 2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg>
                See how it works
              </Link>
            </div>
          </div>
          {/* <p style={s.heroNote}>No credit card required · 2 free sessions on signup</p> */}

          {/* Social proof row */}
          <div style={s.socialProofRow}>
            <div style={s.avatarStack}>
              {['JK','MR','AS','LP','TW'].map(init => (
                <div key={init} style={s.avatar}>{init}</div>
              ))}
              <div style={{...s.avatar, ...s.avatarOverflow}}>+60</div>
            </div>
            <div style={s.socialText}>
              <strong style={s.socialStrong}>65 researchers on the waitlist</strong>
              <span style={s.socialSub}>From Spotify, Intercom, Wise &amp; more</span>
            </div>
            <div style={s.socialDivider} />
            <div style={s.starRating}>
              <div style={s.stars}>{'★★★★★'.split('').map((star, i) => (
                <span key={i} style={s.star}>{star}</span>
              ))}</div>
              <div style={s.ratingText}>4.9 · 23 beta reviews</div>
            </div>
          </div>
        </div>
      </div>

      {/* ── STATS STRIP ── */}
      <div style={s.statsStrip}>
        <div style={s.statsInner} className="scq-stats">
          {STATS.map(stat => (
            <div key={stat.label} style={s.stat}>
              <div style={s.statNumber}>
                {stat.n}<span style={s.statSuffix}>{stat.suffix}</span>
              </div>
              <div style={s.statLabel}>{stat.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── LOGOS STRIP ── */}
      <div style={s.logosWrap}>
        <div style={s.logosInner}>
          <div style={s.logosLabel}>Researchers from these organisations on the waitlist</div>
          <div style={s.logosRow} className="scq-logo-pills">
            {LOGOS.map(org => (
              <div key={org.name} style={s.logoPill}>
                <div style={{...s.logoPillDot, background:org.color}} />
                {org.name}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── PROBLEM ── */}
      <div style={s.problem}>
        <div style={s.problemInner}>
          <div style={s.sectionEyebrow}>The problem</div>
          <h2 style={s.problemH2}>Your research backlog is killing your roadmap.</h2>
          <p style={s.problemP}>
            The average UX researcher spends 60% of their time on synthesis - not insight generation.
            Hours of recordings become days of affinity mapping. By the time findings reach the team,
            the product decision is already made. SenseCritiq collapses that gap.
          </p>
        </div>
      </div>

      {/* ── HOW IT WORKS ── */}
      <div style={s.howWrap}>
        <div style={s.howInner}>
          <div style={s.sectionEyebrowCentre}>How it works</div>
          <h2 style={s.sectionH2Centre}>From recording to insight, same day.</h2>
          <div style={s.pipeline} className="scq-steps">
            {[
              { n:'01', title:'Upload', desc:'Share a recording or transcript from Claude Desktop. Any format. We handle the rest.' },
              { n:'02', title:'Transcribe & clean', desc:'Your recording is transcribed with speaker labels. Noise, filler, and meta-content are stripped so only the signal remains.' },
              { n:'03', title:'Synthesise', desc:'SenseCritiq extracts themes, findings, and verbatim quotes - each one pinned to the speaker and moment it came from.' },
              { n:'04', title:'Report & search', desc:'Download a ready-to-share report. Ask questions across your entire research history in plain language, from Claude.' },
            ].map(step => (
              <div key={step.n} className="scq-card" style={s.pipelineStep}>
                <div style={s.stepN}>{step.n}</div>
                <div style={s.stepNode}>
                  <div style={s.stepNodeDot} />
                </div>
                <div style={s.stepTitle}>{step.title}</div>
                <div style={s.stepDesc}>{step.desc}</div>
              </div>
            ))}
          </div>
          <div style={s.howLink}>
            <Link to="/how-it-works" style={s.textLink}>Full pipeline walkthrough →</Link>
          </div>
        </div>
      </div>

      {/* ── TESTIMONIALS ── */}
      <div style={s.testimonialsWrap}>
        <div style={s.testimonialsInner}>
          <div style={s.sectionEyebrowCentre}>What researchers say</div>
          <h2 style={s.sectionH2Centre}>From the people who actually do this work.</h2>
          <div style={s.testimonialsGrid} className="scq-testimonials">
            {TESTIMONIALS.map((t, i) => (
              <div key={i} className="scq-card" style={{...s.testimonial, ...(t.featured ? s.testimonialFeatured : {})}}>
                {t.featured && <div style={s.testimonialBadge}>Most cited</div>}
                <div style={s.quoteMark}>"</div>
                <p style={s.testimonialText}>{t.text}</p>
                <div style={s.testimonialAuthor}>
                  <div style={s.authorAvatar}>{t.initials}</div>
                  <div>
                    <div style={s.authorName}>{t.name}</div>
                    <div style={s.authorRole}>{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── FEATURES ── */}
      <div style={s.featuresWrap}>
        <div style={s.featuresInner}>
          <div style={s.sectionEyebrow}>Built for trust</div>
          <h2 style={s.featuresH2}>Insights researchers can stake their reputation on.</h2>
          <div style={s.featuresGrid} className="scq-features-strip">
            {[
              { tag:'Evidence-led', title:'Every finding has a source', desc:'No hallucinated insights. Every theme and finding is pinned to a verbatim quote, speaker, and timestamp.' },
              { tag:'Semantic search', title:'Queryable research memory', desc:'Search across every session you\'ve ever run. Ask anything. Get ranked answers with sources - instantly.' },
              { tag:'MCP-native', title:'Built for your workflow', desc:'Use it via the MCP server in Claude Desktop, through the web portal, or pipe findings into Notion.' },
            ].map((f, i, arr) => (
              <div key={f.title} style={{...s.feature, ...(i < arr.length - 1 ? {borderRight:`1px solid ${C.border}`} : {})}} className="scq-feature-item">
                <h3 style={s.featureTitle}>{f.title}</h3>
                <p style={s.featureDesc}>{f.desc}</p>
                <span style={s.featureTag}>{f.tag}</span>
              </div>
            ))}
          </div>
          <div style={s.featuresLink}>
            <Link to="/features" style={s.textLink}>View all features →</Link>
          </div>
        </div>
      </div>

      {/* ── MCP CALLOUT ── */}
      <div style={s.mcpWrap}>
        <div style={s.mcpInner} className="scq-mcp-inner">
          <div style={s.mcpLeft}>
            <div style={s.sectionEyebrow}>MCP server</div>
            <h2 style={s.mcpH2}>Your research, inside Claude.</h2>
            <p style={s.mcpP}>
              Install in Claude Desktop in under two minutes. Upload files, query sessions,
              and generate reports without leaving your AI assistant.
            </p>
            <Link to="/how-it-works" style={s.ctaGhostSm}>Learn about the MCP server →</Link>
          </div>
          <div style={s.mcpRight}>
            <div style={s.codeCard}>
              <div style={s.codeDots}>
                <span style={{...s.codeDot, background:'#FF5F57'}} />
                <span style={{...s.codeDot, background:'#FFBD2E'}} />
                <span style={{...s.codeDot, background:'#28CA41'}} />
              </div>
              <pre style={s.code}>{`// Claude Desktop config
{
  "mcpServers": {
    "sensecritiq": {
      "command": "uvx",
      "args": ["sensecritiq-mcp"],
      "env": {
        "SCQ_API_KEY": "scq_live_..."
      }
    }
  }
}`}</pre>
            </div>
          </div>
        </div>
      </div>

      {/* ── BOTTOM CTA ── */}
      <div style={s.bottomCta}>
        <div style={s.bottomCtaInner} className="scq-bottom-inner">
          <div style={s.bottomGlow} />
          <h2 style={s.bottomH2}>Turn recordings into roadmap fuel.</h2>
          <p style={s.bottomSub}>Two free sessions. No setup fees. Cancel anytime.</p>
          <a href="#waitlist" style={s.ctaPrimary}>Join the waitlist</a>
          <div style={s.ctaSocialProof}>
            {['No credit card required','Free tier forever','Every finding source-cited'].map(item => (
              <span key={item} style={s.ctaSpItem}>
                <span style={{color:C.cyan, fontWeight:700, fontSize:11}}>✓</span> {item}
              </span>
            ))}
          </div>
        </div>
      </div>

      <MarketingFooter />
    </div>
  );
}

const s = {
  root: { background:C.void, minHeight:'100vh', fontFamily:"'Inter',sans-serif", color:C.white },

  // Hero
  hero: { minHeight:'100vh', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', textAlign:'center', padding:'65px 24px 100px', position:'relative', overflow:'hidden' },
  heroBg: { position:'absolute', inset:0, background:'radial-gradient(ellipse 70% 55% at 50% -5%, rgba(0,255,255,0.09) 0%, transparent 65%), radial-gradient(ellipse 40% 35% at 85% 85%, rgba(255,87,34,0.07) 0%, transparent 60%), radial-gradient(ellipse 45% 50% at 8% 65%, rgba(0,255,255,0.04) 0%, transparent 55%)', pointerEvents:'none' },
  particles: { position:'absolute', inset:0, pointerEvents:'none', overflow:'hidden' },
  heroContent: { position:'relative', zIndex:2, maxWidth:760, margin:'0 auto' },

  // Badge
  badge: { display:'inline-flex', alignItems:'center', gap:8, fontFamily:"'Satoshi','Inter',sans-serif", fontWeight:700, fontSize:11, letterSpacing:'0.08em', textTransform:'uppercase', color:C.cyan, border:`1px solid ${C.cyanMid}`, background:C.cyanDim, padding:'6px 16px', borderRadius:100, marginBottom:28 },
  badgeDot: { width:6, height:6, borderRadius:'50%', background:C.cyan, animation:'scqPulse 2s infinite', display:'inline-block' },

  // Hero text
  h1: { fontFamily:"'Satoshi','Inter',sans-serif", fontSize:'clamp(42px,6.5vw,82px)', fontWeight:700, letterSpacing:'-0.04em', lineHeight:1.04, marginBottom:24 },
  h1Accent: { color:C.cyan },
  heroSub: { fontSize:'clamp(16px,2vw,19px)', color:C.muted, maxWidth:500, margin:'0 auto 40px', lineHeight:1.7 },
  heroCtas: { display:'flex', gap:14, justifyContent:'center', flexWrap:'wrap', marginBottom:12 },
  heroNote: { fontSize:12, color:C.muted2, marginBottom:0 },
  wlForm:  { display:'flex', gap:10, flexWrap:'wrap', justifyContent:'center', marginBottom:0 },
  wlInput: { flex:'1 1 220px', maxWidth:280, padding:'13px 16px', borderRadius:9, border:`1px solid ${C.border2}`, background:'rgba(255,255,255,0.06)', color:C.white, fontSize:14, fontFamily:"'Inter',sans-serif", outline:'none' },
  wlBtn:   { padding:'13px 24px', borderRadius:9, background:C.magma, color:'#fff', border:'none', fontFamily:"'Satoshi','Inter',sans-serif", fontWeight:700, fontSize:14, cursor:'pointer', whiteSpace:'nowrap' },
  wlDone:  { fontSize:15, color:C.white, background:'rgba(0,255,255,0.07)', border:`1px solid ${C.cyanBorder}`, borderRadius:10, padding:'13px 20px', display:'inline-block' },
  wlError: { fontSize:12, color:'#FF6B6B', margin:'6px 0 0', width:'100%', textAlign:'center' },
  ctaPrimary: { display:'inline-block', padding:'15px 32px', borderRadius:10, background:C.magma, color:'#fff', fontFamily:"'Satoshi','Inter',sans-serif", fontWeight:700, fontSize:15, textDecoration:'none' },
  ctaGhost: { display:'inline-block', padding:'15px 28px', borderRadius:10, background:'rgba(255,255,255,0.05)', color:C.muted, fontFamily:"'Satoshi','Inter',sans-serif", fontWeight:600, fontSize:15, textDecoration:'none', border:`1px solid ${C.border}` },
  ctaGhostWide: { display:'inline-flex', alignItems:'center', gap:9, padding:'13px 36px', borderRadius:10, background:'#030A3C', color:C.cyan, fontFamily:"'Satoshi','Inter',sans-serif", fontWeight:700, fontSize:15, textDecoration:'none', border:`2px solid ${C.cyan}` },
  ctaGhostSm: { display:'inline-block', padding:'12px 24px', borderRadius:9, background:'rgba(255,255,255,0.05)', color:C.muted, fontFamily:"'Satoshi','Inter',sans-serif", fontWeight:600, fontSize:14, textDecoration:'none', border:`1px solid ${C.border}` },

  // Social proof row
  socialProofRow: { display:'flex', alignItems:'center', justifyContent:'center', gap:20, marginTop:52, paddingTop:36, borderTop:`1px solid ${C.border}`, flexWrap:'wrap' },
  avatarStack: { display:'flex' },
  avatar: { width:34, height:34, borderRadius:'50%', border:`2px solid ${C.void}`, marginLeft:-10, background:C.void3, display:'flex', alignItems:'center', justifyContent:'center', fontFamily:"'Satoshi','Inter',sans-serif", fontSize:11, fontWeight:700, color:C.cyan, flexShrink:0 },
  avatarOverflow: { fontSize:9, color:C.muted, background:'rgba(0,255,255,0.07)' },
  socialText: { textAlign:'left' },
  socialStrong: { fontFamily:"'Satoshi','Inter',sans-serif", fontWeight:700, fontSize:14, color:C.white, display:'block' },
  socialSub: { fontSize:12, color:C.muted },
  socialDivider: { width:1, height:32, background:C.border2 },
  starRating: { display:'flex', flexDirection:'column', alignItems:'center', gap:3 },
  stars: { display:'flex', gap:2 },
  star: { color:'#FFB400', fontSize:13 },
  ratingText: { fontSize:11, color:C.muted },

  // Floating insight cards
  insightCard: { position:'absolute', background:'linear-gradient(135deg, rgba(6,13,74,0.62) 0%, rgba(10,18,96,0.68) 100%)', border:`1px solid ${C.border2}`, borderRadius:12, padding:'16px 18px', textAlign:'left', backdropFilter:'blur(16px)', WebkitBackdropFilter:'blur(16px)', boxShadow:'0 4px 24px rgba(0,0,0,0.50), inset 0 1px 0 rgba(0,255,255,0.10), inset 0 0 0 1px rgba(0,255,255,0.04)', pointerEvents:'none', zIndex:1 },
  icLabel: { fontSize:9, fontWeight:600, letterSpacing:'0.1em', textTransform:'uppercase', color:C.cyan, marginBottom:8, display:'flex', alignItems:'center', gap:5 },
  icTheme: { fontFamily:"'Satoshi','Inter',sans-serif", fontWeight:700, fontSize:12, letterSpacing:'-0.01em', color:C.white, marginBottom:6 },
  icQuote: { fontSize:11, color:C.muted, lineHeight:1.5, fontStyle:'italic' },
  icMeta: { marginTop:10, fontSize:9, color:'rgba(0,255,255,0.4)', display:'flex', alignItems:'center', gap:8 },
  icBars: { display:'flex', gap:2, alignItems:'center' },
  icBar: { height:3, borderRadius:2, background:C.cyan },
  icSeverity: { fontSize:9, color:C.magma, fontWeight:600 },

  // Stats strip
  statsStrip: { background:C.void2, borderTop:`1px solid ${C.border}`, borderBottom:`1px solid ${C.border}` },
  statsInner: { maxWidth:1100, margin:'0 auto', display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:1, background:C.border },
  stat: { background:C.void2, padding:32, textAlign:'center' },
  statNumber: { fontFamily:"'Satoshi','Inter',sans-serif", fontWeight:700, fontSize:38, letterSpacing:'-0.03em', color:C.white, lineHeight:1, marginBottom:6 },
  statSuffix: { color:C.cyan },
  statLabel: { fontSize:13, color:C.muted },

  // Logos strip
  logosWrap: { padding:'52px 24px', textAlign:'center' },
  logosInner: { maxWidth:1100, margin:'0 auto' },
  logosLabel: { fontSize:12, fontWeight:500, letterSpacing:'0.1em', textTransform:'uppercase', color:C.muted2, marginBottom:28 },
  logosRow: { display:'flex', alignItems:'center', justifyContent:'center', gap:10, flexWrap:'wrap' },
  logoPill: { display:'flex', alignItems:'center', gap:8, padding:'10px 20px', borderRadius:100, border:`1px solid ${C.border2}`, background:C.void2, fontSize:13, fontWeight:500, color:C.muted },
  logoPillDot: { width:6, height:6, borderRadius:'50%', flexShrink:0 },

  // Problem
  problem: { background:C.void2, borderTop:`1px solid ${C.border}`, borderBottom:`1px solid ${C.border}`, padding:'80px 24px' },
  problemInner: { maxWidth:680, margin:'0 auto', textAlign:'center' },
  sectionEyebrow: { fontSize:11, fontWeight:700, letterSpacing:'0.12em', textTransform:'uppercase', color:C.cyan, marginBottom:16 },
  sectionEyebrowCentre: { fontSize:11, fontWeight:700, letterSpacing:'0.12em', textTransform:'uppercase', color:C.cyan, marginBottom:16, textAlign:'center' },
  sectionH2Centre: { fontFamily:"'Satoshi','Inter',sans-serif", fontSize:'clamp(26px,3.5vw,44px)', fontWeight:700, letterSpacing:'-0.03em', lineHeight:1.1, marginBottom:48, textAlign:'center' },
  problemH2: { fontFamily:"'Satoshi','Inter',sans-serif", fontSize:'clamp(24px,3.5vw,40px)', fontWeight:700, letterSpacing:'-0.03em', lineHeight:1.15, marginBottom:20 },
  problemP: { fontSize:17, color:C.muted, lineHeight:1.75 },

  // How it works
  howWrap: { padding:'100px 24px 60px' },
  howInner: { maxWidth:1100, margin:'0 auto' },
  pipeline: { display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:0, position:'relative' },
  pipelineStep: { background:'linear-gradient(135deg, rgba(6,13,74,0.65) 0%, rgba(10,18,96,0.72) 100%)', border:`1px solid ${C.border}`, borderRadius:16, padding:'32px 24px 28px', margin:'0 8px', position:'relative', zIndex:1, boxShadow:'0 4px 24px rgba(0,0,0,0.50), inset 0 1px 0 rgba(0,255,255,0.10), inset 0 0 0 1px rgba(0,255,255,0.04)' },
  stepN: { fontFamily:"'Satoshi','Inter',sans-serif", fontSize:10, fontWeight:700, letterSpacing:'0.1em', color:'rgba(0,255,255,0.5)', marginBottom:16 },
  stepNode: { width:36, height:36, borderRadius:'50%', background:C.void3, border:`1.5px solid ${C.border2}`, display:'flex', alignItems:'center', justifyContent:'center', marginBottom:20, position:'relative' },
  stepNodeDot: { width:8, height:8, borderRadius:'50%', background:C.cyan, opacity:0.7 },
  stepTitle: { fontFamily:"'Satoshi','Inter',sans-serif", fontSize:15, fontWeight:700, letterSpacing:'-0.02em', color:C.white, marginBottom:8 },
  stepDesc: { fontSize:13, color:C.muted, lineHeight:1.6 },
  howLink: { textAlign:'center', marginTop:40 },
  textLink: { fontSize:14, color:C.cyan, textDecoration:'none', fontWeight:500 },

  // Testimonials
  testimonialsWrap: { padding:'100px 24px', background:C.void },
  testimonialsInner: { maxWidth:1100, margin:'0 auto' },
  testimonialsGrid: { display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:20, marginTop:0 },
  testimonial: { background:'linear-gradient(135deg, rgba(6,13,74,0.65) 0%, rgba(10,18,96,0.72) 100%)', border:`1px solid ${C.border}`, borderRadius:20, padding:32, position:'relative', boxShadow:'0 4px 24px rgba(0,0,0,0.50), inset 0 1px 0 rgba(0,255,255,0.10), inset 0 0 0 1px rgba(0,255,255,0.04)' },
  testimonialFeatured: { borderColor:'rgba(0,255,255,0.20)', background:'linear-gradient(135deg, rgba(10,18,96,0.72) 0%, rgba(6,13,74,0.65) 100%)' },
  testimonialBadge: { position:'absolute', top:-1, right:24, fontSize:10, fontWeight:700, letterSpacing:'0.05em', textTransform:'uppercase', color:C.void, background:C.cyan, padding:'4px 10px', borderRadius:'0 0 8px 8px', fontFamily:"'Satoshi','Inter',sans-serif" },
  quoteMark: { fontFamily:'Georgia,serif', fontSize:48, lineHeight:0.8, color:C.cyan, opacity:0.3, marginBottom:12 },
  testimonialText: { fontSize:14, lineHeight:1.7, color:'rgba(248,249,250,0.85)', marginBottom:24 },
  testimonialAuthor: { display:'flex', alignItems:'center', gap:12, paddingTop:20, borderTop:`1px solid ${C.border}` },
  authorAvatar: { width:36, height:36, borderRadius:'50%', background:C.void4, border:`1px solid ${C.border2}`, display:'flex', alignItems:'center', justifyContent:'center', fontFamily:"'Satoshi','Inter',sans-serif", fontSize:11, fontWeight:700, color:C.cyan, flexShrink:0 },
  authorName: { fontFamily:"'Satoshi','Inter',sans-serif", fontSize:13, fontWeight:700, color:C.white, letterSpacing:'-0.01em' },
  authorRole: { fontSize:11, color:C.muted, marginTop:1 },

  // Features strip
  featuresWrap: { background:C.void2, borderTop:`1px solid ${C.border}`, borderBottom:`1px solid ${C.border}` },
  featuresInner: { maxWidth:1100, margin:'0 auto', padding:'80px 24px' },
  featuresH2: { fontFamily:"'Satoshi','Inter',sans-serif", fontSize:'clamp(24px,3vw,38px)', fontWeight:700, letterSpacing:'-0.03em', lineHeight:1.1, marginBottom:48, maxWidth:560 },
  featuresGrid: { display:'grid', gridTemplateColumns:'repeat(3,1fr)' },
  feature: { padding:'40px 36px' },
  featureTitle: { fontFamily:"'Satoshi','Inter',sans-serif", fontSize:17, fontWeight:700, color:C.white, marginBottom:10 },
  featureDesc: { fontSize:13, color:C.muted, lineHeight:1.65 },
  featureTag: { display:'inline-block', marginTop:16, fontSize:10, fontWeight:600, padding:'4px 10px', borderRadius:100, background:C.magmaDim, color:C.magma, border:'1px solid rgba(255,87,34,0.2)' },
  featuresLink: { textAlign:'center', marginTop:40 },

  // MCP callout
  mcpWrap: { padding:'100px 24px' },
  mcpInner: { maxWidth:1100, margin:'0 auto', display:'grid', gridTemplateColumns:'1fr 1fr', gap:80, alignItems:'center' },
  mcpLeft: {},
  mcpH2: { fontFamily:"'Satoshi','Inter',sans-serif", fontSize:'clamp(26px,3vw,40px)', fontWeight:700, letterSpacing:'-0.03em', lineHeight:1.1, marginBottom:16 },
  mcpP: { fontSize:16, color:C.muted, lineHeight:1.75, marginBottom:28 },
  mcpRight: {},
  codeCard: { background:'#020818', border:`1px solid rgba(0,255,255,0.12)`, borderRadius:16, padding:'20px 24px' },
  codeDots: { display:'flex', gap:6, marginBottom:16 },
  codeDot: { width:10, height:10, borderRadius:'50%' },
  code: { margin:0, fontSize:13, color:'rgba(248,249,250,0.75)', lineHeight:1.7, overflowX:'auto', fontFamily:"'JetBrains Mono','Fira Code',monospace" },

  // Bottom CTA
  bottomCta: { padding:'80px 24px 100px' },
  bottomCtaInner: { maxWidth:1052, margin:'0 auto', background:`linear-gradient(135deg, ${C.void3} 0%, #0A0E4A 100%)`, border:`1px solid ${C.border2}`, borderRadius:24, padding:'80px 48px', textAlign:'center', position:'relative', overflow:'hidden' },
  bottomGlow: { position:'absolute', top:'-40%', left:'50%', transform:'translateX(-50%)', width:500, height:400, borderRadius:'50%', background:'radial-gradient(circle, rgba(0,255,255,0.06) 0%, transparent 70%)', pointerEvents:'none' },
  bottomH2: { fontFamily:"'Satoshi','Inter',sans-serif", fontSize:'clamp(28px,4vw,48px)', fontWeight:700, letterSpacing:'-0.03em', marginBottom:16, position:'relative' },
  bottomSub: { fontSize:17, color:C.muted, marginBottom:36, position:'relative' },
  ctaSocialProof: { display:'flex', alignItems:'center', justifyContent:'center', gap:28, marginTop:32, paddingTop:28, borderTop:`1px solid ${C.border}`, flexWrap:'wrap', position:'relative' },
  ctaSpItem: { fontSize:12, color:C.muted, display:'flex', alignItems:'center', gap:6 },
};
